import { createContext, useContext, useReducer } from 'react';
import type { ReactNode } from 'react';
import { SET_PAGE_TITLE } from './actionTypes';
import type { PageTitleState, PageTitleAction } from '@/types';

const PageTitleContext = createContext<PageTitleState | null>(null);

const PageTitleDispatchContext = createContext<React.Dispatch<PageTitleAction> | null>(null);

const initialState: PageTitleState = '';

interface PageTitleProviderProps {
  children: ReactNode;
}

export function PageTitleProvider({ children }: PageTitleProviderProps) {
  const [pageTitle, dispatch] = useReducer(pageTitleReducer, initialState);

  return (
    <PageTitleContext.Provider value={pageTitle}>
      <PageTitleDispatchContext.Provider value={dispatch}>
        {children}
      </PageTitleDispatchContext.Provider>
    </PageTitleContext.Provider>
  );
}

export function usePageTitle(): PageTitleState {
  const pageTitle = useContext(PageTitleContext);
  if (pageTitle === null) {
    throw new Error('usePageTitle must be used within PageTitleProvider');
  }
  return pageTitle;
}

export function usePageTitleDispatch(): React.Dispatch<PageTitleAction> {
  const dispatch = useContext(PageTitleDispatchContext);
  if (!dispatch) {
    throw new Error('usePageTitleDispatch must be used within PageTitleProvider');
  }
  return dispatch;
}

function pageTitleReducer(_pageTitle: PageTitleState, action: PageTitleAction): PageTitleState {
  switch (action.type) {
    case SET_PAGE_TITLE: {
      return action.payload;
    }
    default: {
      throw Error('Unknown action: ' + JSON.stringify(action));
    }
  }
}
